import { AutoSelection, Combobox, EmptySearchResult, LegacyStack, Listbox, Tag, Text } from "@shopify/polaris";
import { useCallback, useMemo, useState } from "react";
import { useAppQuery } from "../../../hooks/index.js";

interface ITags {
  tags: String[];
}

function SelectedTagsField({ input, labelHidden, disabled, meta }) {
  const [selectedTags, setSelectedTags] = useState<string[]>(input.value || []);
  const [value, setValue] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const { error, touched, submitFailed } = meta
  const showError = (touched || submitFailed) && error;

  const { isLoading, data } = useAppQuery<ITags, Error>({
    url: "/api/products/tags",
    reactQueryOptions: {
      staleTime: Infinity
    },
    fetchInit: {}
  });

  const handleActiveOptionChange = useCallback(
    (activeOption: string) => {
      const activeOptionIsAction = activeOption === value;

      if (!activeOptionIsAction && !selectedTags.includes(activeOption)) {
        setSuggestion(activeOption);
      } else {
        setSuggestion("");
      }
    },
    [value, selectedTags]
  );

  const updateSelection = useCallback(
    (selected: string) => {
      const nextSelectedTags = new Set([...selectedTags]);

      if (nextSelectedTags.has(selected)) {
        nextSelectedTags.delete(selected);
      } else {
        nextSelectedTags.add(selected);
      }
      setSelectedTags([...nextSelectedTags]);
      setValue("");
      setSuggestion("");
      input.onChange([...nextSelectedTags]);
    },
    [selectedTags, input]
  );

  const removeTag = useCallback(
    (tag: string) => () => {
      updateSelection(tag);
    },
    [updateSelection]
  );

  const getAllTags = useCallback(() => {
    const savedTags = data?.tags || [];
    return [...new Set([...savedTags, ...selectedTags].sort())];
  }, [selectedTags, data]);

  const formatOptionText = useCallback(
    (option: string) => {
      const trimValue = value.trim().toLocaleLowerCase();
      const matchIndex = option.toLocaleLowerCase().indexOf(trimValue);

      if (!value || matchIndex === -1) return option;

      const start = option.slice(0, matchIndex);
      const highlight = option.slice(matchIndex, matchIndex + trimValue.length);
      const end = option.slice(matchIndex + trimValue.length, option.length);

      return (
        <p>
          {start}
          <Text fontWeight="bold" as="span" variant={"bodyMd"}>
            {highlight}
          </Text>
          {end}
        </p>
      );
    },
    [value]
  );

  const options = useMemo(() => {
    let list;
    const allTags = getAllTags();
    const filterRegex = new RegExp(value, "i");

    if (value) {
      list = allTags.filter((tag) => tag.match(filterRegex));
    } else {
      list = allTags;
    }

    return [...list];
  }, [value, getAllTags]);

  const verticalContentMarkup =
    selectedTags?.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        {selectedTags && selectedTags?.map((tag) => (
          <Tag key={`option-${tag}`} onRemove={removeTag(tag)}>
            {tag}
          </Tag>
        ))}
      </LegacyStack>
    ) : null;

  const optionMarkup =
    options.length > 0
      ? options.map((option) => {
        return (
          <Listbox.Option
            key={option}
            value={option}
            selected={selectedTags.includes(option)}
            accessibilityLabel={option}
          >
            <Listbox.TextOption selected={selectedTags.includes(option)}>
              {formatOptionText(option)}
            </Listbox.TextOption>
          </Listbox.Option>
        );
      })
      : null;

  const noResults = value && !getAllTags().includes(value);

  const actionMarkup = noResults ? (
    <Listbox.Action value={value}>{`Add "${value}"`}</Listbox.Action>
  ) : null;

  const emptyStateMarkup = optionMarkup ? null : (
    <EmptySearchResult
      title=""
      description={`No tags found matching "${value}"`}
    />
  );

  const listboxMarkup =
    optionMarkup || actionMarkup || emptyStateMarkup ? (
      <Listbox
        autoSelection={AutoSelection.None}
        onSelect={updateSelection}
        onActiveOptionChange={handleActiveOptionChange}
      >
        {actionMarkup}
        {optionMarkup}
      </Listbox>
    ) : null;

  return (
    <Combobox
      allowMultiple
      activator={
        <Combobox.TextField
          autoComplete="off"
          label="Selected tags"
          labelHidden={labelHidden}
          value={value}
          suggestion={suggestion}
          placeholder="Search tags"
          verticalContent={verticalContentMarkup}
          onChange={setValue}
          error={showError}
          onBlur={() => input.onBlur()}
        />
      }
    >
      {listboxMarkup}
    </Combobox >
  );
}


export default SelectedTagsField;